import React, { useEffect, useState } from "react";
import { BacsModal } from "@components";
import {
  MastercardIcon,
  VisaIcon,
  AmexIcon,
  KlarnaIcon,
  BankIcon,
  CardGeneralIcon,
} from "./components/PaymentIcons";
import {
  PageTemplate,
  PageBody,
  PageFooter,
  Summary,
} from "../shared-components";
import { useNavigate, useOutletContext } from "react-router-dom";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { useNoBacsLink } from "@flags";
import { getSoleCheckoutMethodRoute } from "../../hooks/flowRouting";

const PRChooseMethod = () => {
  const {
    id,
    merchant,
    cardPaymentAllowed,
    klarnaPaymentAllowed,
    openBankingPaymentAllowed,
    amexPaymentAllowed,
    statement,
    amount,
    reference,
    summaryData,
    settlementDiscount,
    flowId,
  } = useOutletContext();
  const navigate = useNavigate();

  // Destructure summary data from context
  const {
    invoiceTotal,
    invoiceCount,
    creditNoteTotal,
    creditNoteCount,
    totalToPay,
  } = summaryData;

  const noBacsLink = useNoBacsLink();

  const hasAmount = Boolean(amount) && Number(amount) !== 0;

  useEffect(() => {
    if (!hasAmount) {
      navigate(`/${flowId}/${id}/`);
    }
  }, [hasAmount, id, flowId, navigate]);

  const options = [
    {
      route: "paybybank",
      label: "Bank",
      description: "Authorise the payment via online banking",
      valid: openBankingPaymentAllowed,
      leadingIcon: BankIcon,
    },
    {
      route: "paybycard",
      label: "Card",
      description: "Pay with corporate / personal card details",
      valid: cardPaymentAllowed,
      leadingIcon: CardGeneralIcon,
      icons: [
        amexPaymentAllowed ? AmexIcon : null,
        MastercardIcon,
        VisaIcon,
      ].filter(Boolean),
    },
    {
      route: "paybybnpl",
      label: "Buy Now Pay Later",
      description: "Pay via Klarna",
      valid: klarnaPaymentAllowed,
      icons: [KlarnaIcon],
    },
  ];

  // A merchant checkout allowing a single method skips this chooser. Not
  // when there is nothing to pay: the effect above is sending the shopper
  // to the base route, and this one would override it.
  const soleRoute = hasAmount
    ? getSoleCheckoutMethodRoute({ flowId, options })
    : null;
  useEffect(() => {
    if (soleRoute) {
      navigate(`/${flowId}/${id}/${soleRoute}`, { replace: true });
    }
  }, [soleRoute, flowId, id, navigate]);

  if (soleRoute) {
    return null;
  }

  return (
    <PageTemplate className="flex flex-col lg:flex-row lg:h-screen lg:overflow-hidden">
      <PageBody>
        <div className="px-5 lg:px-0 pb-5">
          <h3 className="text-xl/8 font-semibold">Select payment method</h3>
          {!noBacsLink && (
            <h4 className="text-sm text-zinc-500">
              <BacsModal merchant={merchant} reference={reference} inline />
            </h4>
          )}
        </div>
        <div className="flex flex-col gap-4 px-5 lg:px-0">
          {options
            .filter((option) => option.valid)
            .map((option) => (
              <button
                className="flex items-start justify-between cursor-pointer relative z-0 border border-zinc-200 rounded-xl overflow-hidden gap-4 bg-white px-4 py-5"
                key={option.route}
                onClick={() => navigate(`/${flowId}/${id}/${option.route}`)}
              >
                {option.leadingIcon && (
                  <div className="shrink-0 size-8 rounded-full bg-zinc-200 flex items-center justify-center">
                    <option.leadingIcon className="block" />
                  </div>
                )}
                <div className="flex flex-col gap-0.5 flex-1 min-w-0 items-start">
                  <span className="font-semibold">{option.label}</span>
                  <span className="text-sm text-zinc-500 whitespace-normal text-left">
                    {option.description}
                  </span>
                  {option.icons?.length > 0 && (
                    <div className="flex items-center gap-2 mt-1">
                      {option.icons.map((Icon, index) => (
                        <Icon key={index} className="size-7" />
                      ))}
                    </div>
                  )}
                </div>
                <div className="self-center flex items-center justify-center shrink-0">
                  <ChevronRightIcon className="size-3.5" strokeWidth={3} />
                </div>
              </button>
            ))}
        </div>
      </PageBody>
      <PageFooter className="hidden lg:block">
        <div className="pb-7 space-y-2 hidden lg:block">
          <h2 className="text-xl font-medium">Payment summary</h2>
        </div>

        <Summary
          invoiceTotal={invoiceTotal}
          invoiceCount={invoiceCount}
          creditNoteTotal={creditNoteTotal}
          creditNoteCount={creditNoteCount}
          totalToPay={Math.max(0, totalToPay - settlementDiscount)}
          creditOnAccount={statement?.creditOnAccount || 0}
          hasStatement={!!statement}
          amountRequested={amount}
          settlementDiscount={settlementDiscount}
        />
      </PageFooter>
    </PageTemplate>
  );
};

export default PRChooseMethod;
